import Handlebars from 'handlebars';
import Highcharts from 'highcharts/highstock';
import 'highcharts/modules/exporting';
import 'highcharts/modules/no-data-to-display';
import 'highcharts/modules/accessibility';

import AMApp from '../../../../../../../src/global/app';

export class FondsDetailZusammensetzung {
  constructor(element, options = {}) {
    this.element = element;

    this.settings = Object.assign({
      routeFondsdetail: AMApp.langGetParam + 'eID=fonds&function=fondsdetail&isin=',
      routeZusammensetzung: AMApp.langGetParam + 'eID=fonds&function=zusammensetzung&fondsId=',
      routeKaufen: '?eID=fonds&function=kaufen&fondsId=',
      fileadminPath: '/fileadmin/',
      routeConfig: AMApp.langGetParam + 'eID=fonds&function=getConfig&isin='
    }, options);
  }

  init(isin, fondsAndiId, glossData, fondsData, fondsCategory, fondsCategoriesOrder) {
    this.config = {};
    this.isin = isin;
    this.fondsData = fondsData;
    this.fondsCategory = fondsCategory;
    this.fondsCategoriesOrder = fondsCategoriesOrder;

    this.getZusammensetzungConfig().then(config => {
      this.config = config;

      this.getZusammensetzungData(fondsAndiId).then(data => {
        this.createZusammensetzungChartContainer(data, glossData);
      });
    });
  }

  async getZusammensetzungConfig() {
    try {
      const response = await fetch(`${this.settings.routeConfig}${this.isin}`);
      return response.json();
    } catch (error) {
      console.error('Error fetching config:', error);
      return {};
    }
  }

  async getZusammensetzungData(fondsAndiId) {
    try {
      const response = await fetch(`${this.settings.routeZusammensetzung}${fondsAndiId}`);

      if (!response.ok) throw new Error('Network response was not ok');

      return response.json();
    } catch (error) {
      console.error('error', error);
    }
  }

  async createZusammensetzungChartContainer(data, gloss) {
    const zusammensetzungRaw = document.getElementById('zusammensetzung_template')?.innerHTML || '';
    let charts = [];

    try {
      Object.entries(data).forEach(([chart, chartData]) => {
        const chartContainerId = this.createChartKey(chart, this.config);
        let titleGloss = '';

        if (chartData.title === 'Rohstoffe im LBBW Index') {
          titleGloss = gloss.resourceIndex;
        }

        charts.push({
          container: chartContainerId,
          title: chartData.title,
          data: chartData.data,
          gloss: titleGloss
        });
      });

      charts = this.orderChartContainer(charts, this.config);

      const zusammensetzungCompiled = Handlebars.compile(this.toHandlebarsDelimiters(zusammensetzungRaw));
      const zusammensetzungTemplate = zusammensetzungCompiled({ charts: charts });

      const target = document.getElementById('zusammensetzung_target');
      if (target) target.innerHTML = zusammensetzungTemplate;

      setTimeout(() => {
        this.prepareZusammensetzungChartData(charts);
      }, 500);
    } catch (error) {
      console.error('Error creating chart container:', error);
    }
  }

  orderChartContainer(charts, config) {
    let chartsOrdered = [];
    let order = {};

    if (config.chart_order && config.chart_order[0] === '') {
      if (this.fondsData['INSD_NAME'] === 'LBBW Aktien Deutschland') {
        order = this.fondsCategoriesOrder.LBBWAktienDeutschland;
      } else if (this.fondsData['INSD_NAME'] === 'LBBW Rohstoffe & Ressourcen') {
        order = this.fondsCategoriesOrder.LBBWRohstoffeRessourcen;
      } else if (this.fondsData['INSD_NAME'].includes('BW Portfolio')) {
        order = this.fondsCategoriesOrder.BWPortfolio;
      } else {
        order = this.fondsCategoriesOrder[this.fondsCategory];
      }
    } else {
      order.chartOrder = config.chart_order;
    }

    if (charts.length && order?.chartOrder) {
      order.chartOrder.forEach(orderKey => {
        orderKey = orderKey.replace('|pie', '').replace('|bar', '').replace('TOP', '').toLowerCase();
        charts.forEach(chart => {
          const chartKey = this.converters.convertChartTitle(chart.title).replace('top', '');
          if (orderKey === chartKey ||
            (orderKey.includes('restlaufzeit') && chartKey.includes('restlaufzeit')) ||
            (orderKey === 'waehrungrenten' && chartKey === 'waehrungen(renten)') ||
            (orderKey === 'waehrungrenten' && chartKey === 'waehrungenrenten') ||
            (orderKey === 'waehrungaktien' && chartKey === 'waehrungenaktien')) {
            chartsOrdered.push(chart);
          }
        });
      });

      return chartsOrdered;
    }

    return charts;
  }

  createChartKey(chart, config) {
    let key = '';

    if (config.chart_order && config.chart_order[0] !== '') {
      config.chart_order.forEach((type) => {
        const chartKey = type.split('|')[0];
        const chartType = type.split('|')[1];

        if (chart === chartKey) {
          key = `chart_${chartType}_${chartKey}`;
        }
      });
    } else {
      if (chart === 'Portfoliostruktur' ||
        !chart.toLowerCase().includes('top') ||
        chart.toLowerCase().includes('rohstoffe')) {
        key = `chart_pie_${chart}`;
      } else {
        key = `chart_bar_${chart}`;
      }
    }

    return key;
  }

  prepareZusammensetzungChartData(charts) {
    charts.forEach(chart => {
      let categories = [];
      let series = {
        data: []
      };

      Object.entries(chart.data).forEach(([category, value]) => {
        categories.push(category);
        series.data.push({
          name: category,
          y: value
        });
      });

      if (chart.container.includes('chart_pie_')) {
        this.createChartZusammensetzungPie(chart.container, categories, series.data);
      } else {
        this.createChartZusammensetzungBar(chart.container, categories, series);
      }
    });
  }

  /**
   * Creates a pie chart for "Zusammensetzung" (composition) using the provided data and configuration.
   *
   * This function is a part of the AMApp library and is used to generate a specific type of pie chart
   * that reflects the composition or distribution of data. It utilizes an external charting library to render
   * the visualization and can be customized based on the input parameters and configuration passed.
   *
   * @param container
   * @param categories
   * @param series
   *                               typically represents a slice of the chart with its corresponding value and label.
   *                           Options might include properties like colors, legend, tooltips, and size.
   * @throws {Error} If the container ID is invalid or if the data array is empty or incorrectly formatted.
   */
  createChartZusammensetzungBar(container, categories, series) {
    const options = {
      accessibility: {
        landmarkVerbosity: 'one',
        screenReaderSection: {
          beforeChartFormat: '<p>Balkendiagramm der Fonds-Zusammensetzung.</p>'
        },
        point: {
          valueDescriptionFormat: '{xDescription}, {value}.',
          descriptionFormatter: function(point) {
            // Formatiert den Wert als Prozent mit 2 Dezimalstellen
            const formattedValue = (point.y * 100).toFixed(2).replace('.', ',') + ' %';
            return `${point.name}: ${formattedValue}`;
          }
        },
        series: {
          pointDescriptionEnabledThreshold: false,
          describeSingleSeries: true,
        },
        keyboardNavigation: {
          enabled: true
        }
      },
      chart: {
        type: 'bar',
      },
      exporting: {
        enabled: false,
      },
      title: {
        text: '',
      },
      tooltip: {
        enabled: true,
        formatter: function () {
          const name = this.point.name || this.series.name || '';
          return '<b>' + name + ': ' + Highcharts.numberFormat((this.y * 100), 2, ',') + ' %</b>';
        },
        style: {
          backgroundColor: '#f7f7f7',
          color: '#001729',
        },
        stickOnContact: true,
        hideDelay: 500,
      },
      plotOptions: {
        series: {
          shadow: false,
          stacking: 'normal',
          animation: true,
          accessibility: {
            exposeAsGroupOnly: false,
          }
        },
        bar: {
          pointWidth: 12,
          borderRadius: 0,
          borderWidth: 0,
          pointPadding: 0,
          groupPadding: 0.5,
          dataLabels: {
            align: 'left',
            color: '#001729',
            distance: 20,
            overflow: 'none',
            crop: false,
            enabled: true,
            style: {
              fontSize: '12px',
              fontWeight: 'normal'
            },
            y: -14,
            x: 1,
            formatter: function () {
              return this.name + ': <b>' + Highcharts.numberFormat((this.y * 100), 2, ',') + ' %</b>';
            }
          }
        }
      },
      xAxis: {
        categories: [],
        title: {
          text: null
        },
        labels: {
          style: {
            color: '#001729',
            fontSize: '13px'
          },
          enabled: false,
          align: 'right'
        }
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          formatter: function () {
            return Highcharts.numberFormat((this.value * 100), 1) + ' %';
          },
          enabled: true
        },
        showFirstLabel: true
      },
      legend: {
        enabled: false
      },
      series: [],
    }

    options.xAxis.categories = categories;
    options.series.push(series);

    Highcharts.chart(container, options)
  }

  /**
   * Prepares the data for rendering the Zusammensetzung chart.
   *
   * This method processes and formats the data required for constructing
   * the Zusammensetzung chart by aggregating and organizing it as per
   * the chart's requirements.
   *
   * @function
   * @name prepareZusammensetzungChartData
   * @memberof AMApp.prototype
   * @returns {Object} - The formatted data ready to be used in the chart.
   * @param container
   * @param categories
   * @param data
   */
  createChartZusammensetzungPie(container, categories, data) {
    let size = '60%';
    let innerSize = '75%';
    let height = 300;

    if (window.innerWidth >= AMApp.CONSTANTS.BREAKPOINT.MD) {
      size = '100%';
      innerSize = '80%';
    }

    if (window.innerWidth >= AMApp.CONSTANTS.BREAKPOINT.XL) {
      size = '90%';
      height = 400;
    }

    const options = {
      styledMode: true,
      chart: {
        type: 'pie',
        marginLeft: 0,
        height: height
      },
      credits: { enabled: false },
      title: {
        text: ''
      },
      tooltip: {
        formatter: function () {
          return '<b>' + this.point.name + ': </b>' + Highcharts.numberFormat(this.point.y * 100, 1) + ' %';
        },
        style: {
          backgroundColor: '#f7f7f7',
          color: '#001729',
        },
        stickOnContact: true,
        hideDelay: 500,
      },
      plotOptions: {
        series: {
          allowPointSelect: true,
          borderRadius: 0,
          cursor: 'pointer',
          shadow: false,
          dataLabels: {
            formatter: function () {
              //return Highcharts.numberFormat(this.y * 100, 1) + ' %';
              return this.point.name;
            },
            distance: 15
          }
        }
      },
      series: []
    };

    const series = {
      data: data,
      size: size,
      innerSize: innerSize
    };
    options.series.push(series);

    Highcharts.chart(container, options);
  }

  toHandlebarsDelimiters(str) {
    return str
      .replaceAll('[[[', '{{{')
      .replaceAll(']]]', '}}}')
      .replaceAll('[[', '{{')
      .replaceAll(']]', '}}');
  }

  converters = {
    createList: (data) => {
      let list = '';
      data.forEach(item => {
        if (item !== '' && item !== ' ') {
          list += `<li>${item}</li>`;
        }
      });
      return list;
    },

    cleanString: (string) => {
      return string.replace(/ /g, '_')
        .replace(/ä/g, 'ae')
        .replace(/ü/g, 'ue')
        .replace(/ö/g, 'oe')
        .replace(/ß/g, 'ss')
        .toLowerCase();
    },

    convertChartTitle: (string) => {
      return string.replace(/ /g, '')
        .replace(/ä/g, 'ae')
        .replace(/ü/g, 'ue')
        .replace(/ö/g, 'oe')
        .replace(/ß/g, 'ss')
        .replace(/10/g, '')
        .replace(/5/g, '')
        .toLowerCase();
    }
  };
}